import {Extent} from 'ol/extent';

/**
 * Brief state metadata
 *
 * @property {string} id - The state's unique id
 * @property {string} name - The state's full name
 * @property {Extent} extent - Geographical boundary of the state
 */
export interface State {
  id: string;
  name: string;
  extent: Extent;
  new?: boolean;
}

/**
 * WMS metadata
 *
 * @property {string} baseUrl - The base url of the WMS without any url parameters
 * @property {string} licence - Licence used by the WMS
 * @property {string} licenceUrl - Url pointing to WMS licence information
 * @property {string} attribution - Attribution to be shown when WMS is used
 * @property {string} state - The state this WMS should be used for
 * @property {'WMS' | 'WMTS'} type - The type of the WMS
 * @property {string} layer - Layer to be used with the WMS
 */
export interface WMSInfo {
  baseUrl: string;
  licence: string;
  licenceUrl: string;
  attribution: string;
  state: string;
  type: 'WMS' | 'WMTS';
  layer: string;
}

/**
 * An assignment for a number of plots to either an operator's task list or watch list
 *
 * @property {number[]} plotIds - An array of plot ids to be assigned
 * @property {'scapi' | 'sfb'} method - The assignment can be made for sCAPI of sFB
 * @property {string} operator - Username of the operator the plots are being assigned to
 * @property {'task-list' | 'watch-list'} target - Assignments can be made to either task list or watch list
 * @property {string} mapIndex - (optional) A map index for the assignment
 */
export interface Assignment {
  plotIds: number[];
  method: 'scapi' | 'sfb';
  operator: string;
  target: 'task-list' | 'watch-list';
  mapIndex?: string
}

export type MetadataLabel = 'BEWEIDUNG' | 'GPS' | 'INTERVENTIONEN' | 
  'bejagungsschneise' | 'nc_festg' | 'statusdgl' | 'bodennutzung' | 'massnahmen' | 'mischpartner' | 'zusatzangaben' | 
  'KZ_SCHNEISE' | 'KZ_AGRIPHOTO' | 'KZ_VNS' | 
  'OEKO' | 'NCODE_LASTYEAR';


export type MetadataValue = {
    value: boolean | string | null;
    row: number;
}

export class Metadata {
  booleanLabels: string[] = ['BEWEIDUNG', 'GPS', 'bejagungsschneise', 'KZ_SCHNEISE', 'KZ_AGRIPHOTO', 'KZ_VNS', 'OEKO'];
  values: {
    [key in MetadataLabel]: MetadataValue;
  };

  public rowDefinition: { [key: string]: number } = {
    'BEWEIDUNG': 0,
    'GPS': 0,
    'INTERVENTIONEN': 1,

    'bejagungsschneise': 0,
    'nc_festg': 1,
    'statusdgl': 0,
    'bodennutzung': 0,
    'massnahmen': 1,
    'mischpartner': 1,
    'zusatzangaben': 2,

    'KZ_SCHNEISE': 0,
    'KZ_AGRIPHOTO': 1,
    'KZ_VNS': 0,

    'OEKO': 0,
    'NCODE_LASTYEAR': 0
  };

/**
 * Creates a new Metadata object with the given metadata object and labels.
 * Has to be dynamic, because each state has different metadata labels
 * @param {any} metadataObject - The object containing the metadata values
 * @param {MetadataLabel[]} labels - The labels of the metadata values to be included in the object
 */
  constructor(metadataObject: any, labels: MetadataLabel[]) {
    console.log('metadata object', metadataObject);
    this.values = labels.reduce((obj, label) => {
      let value = metadataObject[label];

      if (this.booleanLabels.includes(label) && value != undefined) {
        if (value.toString().toLowerCase() === 'j' || value.toString() === '1') { value = true; }
        else if (value.toString().toLowerCase() === 'n' || value.toString() === '0') { value = false; }
        else { value = null; }
      } 

      (obj as Record<MetadataLabel, any>)[label] = {
        value: this.booleanLabels.includes(label) && value != null ? !!value : value,
        row: this.rowDefinition[label]
      };

    return obj;
    }, {}) as Record<MetadataLabel, MetadataValue>;
  }
}